import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isCurrentPage = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getNavLinkClass = (path: string) => {
    const baseClass = "flex-1 text-center relative transition-all duration-300 hover:text-[#0B6054] group";
    const currentClass = isCurrentPage(path) ? "font-bold" : "font-medium";
    return `${baseClass} ${currentClass}`;
  };

  const getMobileNavLinkClass = (path: string) => {
    const baseClass = "flex items-center px-3 py-3 text-[#1A2A44] hover:text-[#0B6054] hover:bg-[#F6F6F6] rounded-lg transition-all duration-200 group";
    const currentClass = isCurrentPage(path) ? "font-bold bg-[#F6F6F6]" : "font-medium";
    return `${baseClass} ${currentClass}`;
  };

  return (
    <nav className="sticky top-0 z-50 bg-[#F6F6F6]">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 text-[#1A2A44] text-[20px] font-[500] leading-[32px]">
          {/* Mobile Menu Button - Left Side */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-[#1A2A44] hover:text-[#0F1A2A] focus:outline-none focus:text-[#0F1A2A] p-2 rounded-lg hover:bg-white/50 transition-all duration-300"
              aria-label="Toggle mobile menu"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute top-0 left-0 w-6 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2.5' : 'translate-y-0'}`}></span>
                <span className={`absolute top-2.5 left-0 w-6 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`absolute top-5 left-0 w-6 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2.5' : 'translate-y-0'}`}></span>
              </div>
            </button>
          </div>

          {/* All Navigation Elements in One Div - Desktop & Tablet */}
          <div className="hidden lg:flex w-full justify-between items-center">
            <Link 
              to="/mining-stocks" 
              className={getNavLinkClass('/mining-stocks')}
              onClick={scrollToTop}
            >
              <span className="inline-block relative">
                Top Mining Stocks
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full"></span>
              </span>
            </Link>
            <Link 
              to="/research" 
              className={getNavLinkClass('/research')}
              onClick={scrollToTop}
            >
              <span className="inline-block relative">
                Research
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full"></span>
              </span>
            </Link>
            <Link 
              to="/resources" 
              className={getNavLinkClass('/resources')}
              onClick={scrollToTop}
            >
              <span className="inline-block relative">
                Resources
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full"></span>
              </span>
            </Link>
            <div className="flex-1 flex justify-center">
              <Link to="/" onClick={scrollToTop}>
                <img 
                  src="/images/Logo.webp" 
                  alt="THE WEALTHY MINER" 
                  className="h-[72px] w-[178.539px] lg:h-[80px] lg:w-[200px] xl:h-[87.575px] xl:w-[217.16px]"
                />
              </Link>
            </div>
            <Link 
              to="/about" 
              className={getNavLinkClass('/about')}
              onClick={scrollToTop}
            >
              <span className="inline-block relative">
                About Us
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full"></span>
              </span>
            </Link>
            <Link 
              to="/contact" 
              className={getNavLinkClass('/contact')}
              onClick={scrollToTop}
            >
              <span className="inline-block relative">
                Contact Us
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full"></span>
              </span>
            </Link>
            <Link 
              to="/blog" 
              className={getNavLinkClass('/blog')}
              onClick={scrollToTop}
            >
              <span className="inline-block relative">
                Blog
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full"></span>
              </span>
            </Link>
          </div>

          {/* Mobile & Tablet Logo - Center */}
          <div className="lg:hidden flex-1 flex justify-center">
            <Link to="/" onClick={scrollToTop} className="hover:opacity-80 transition-opacity duration-200">
              <img 
                src="/images/Logo.webp" 
                alt="THE WEALTHY MINER" 
                className="h-[72px] w-[178.539px] lg:h-[80px] lg:w-[200px] xl:h-[87.575px] xl:w-[217.16px]"
              />
            </Link>
          </div>

          {/* Spacer for mobile layout to balance the hamburger button */}
          <div className="lg:hidden w-10"></div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              onClick={toggleMobileMenu}
            ></div>
            
            {/* Menu Panel */}
            <div className="absolute top-0 left-0 w-80 h-full bg-white shadow-2xl transform transition-transform duration-300 ease-out">
              {/* Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#1A2A44]">Menu</h2>
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Menu Items */}
              <div className="p-4">
                <div className="space-y-2">
                  {/* Left Side Links */}
                  <div className="mb-6">
                    <div className="space-y-1">
                      <Link 
                        to="/mining-stocks" 
                        className={getMobileNavLinkClass('/mining-stocks')}
                        onClick={() => { toggleMobileMenu(); scrollToTop(); }}
                      >
                        <span className="text-lg">Top Mining Stocks</span>
                        <svg className="ml-auto w-4 h-4 text-gray-400 group-hover:text-[#1A2A44] transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      <Link 
                        to="/research" 
                        className={getMobileNavLinkClass('/research')}
                        onClick={() => { toggleMobileMenu(); scrollToTop(); }}
                      >
                        <span className="text-lg">Research</span>
                        <svg className="ml-auto w-4 h-4 text-gray-400 group-hover:text-[#1A2A44] transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      <Link 
                        to="/resources" 
                        className={getMobileNavLinkClass('/resources')}
                        onClick={() => { toggleMobileMenu(); scrollToTop(); }}
                      >
                        <span className="text-lg">Resources</span>
                        <svg className="ml-auto w-4 h-4 text-gray-400 group-hover:text-[#1A2A44] transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  
                    <div className="space-y-1">
                      <Link 
                        to="/about" 
                        className={getMobileNavLinkClass('/about')}
                        onClick={() => { toggleMobileMenu(); scrollToTop(); }}
                      >
                        <span className="text-lg">About Us</span>
                        <svg className="ml-auto w-4 h-4 text-gray-400 group-hover:text-[#1A2A44] transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      <Link 
                        to="/contact" 
                        className={getMobileNavLinkClass('/contact')}
                        onClick={() => { toggleMobileMenu(); scrollToTop(); }}
                      >
                        <span className="text-lg">Contact Us</span>
                        <svg className="ml-auto w-4 h-4 text-gray-400 group-hover:text-[#1A2A44] transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      <Link 
                        to="/blog" 
                        className={getMobileNavLinkClass('/blog')}
                        onClick={() => { toggleMobileMenu(); scrollToTop(); }}
                      >
                        <span className="text-lg">Blog</span>
                        <svg className="ml-auto w-4 h-4 text-gray-400 group-hover:text-[#1A2A44] transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">© 2025 The Wealthy Miner</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
